/**
 * ExamMode - Main exam page component
 * Refactored to use smaller sub-components
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Flag, Loader2, LogOut, CheckCircle2 } from 'lucide-react';
import ContentProtection from '@/components/ContentProtection';

// Sub-components
import MockExamSelector from '@/components/exam/MockExamSelector';
import BreakScreen from '@/components/exam/BreakScreen';
import ExamNavigation from '@/components/exam/ExamNavigation';
import QuestionView from '@/components/exam/QuestionView';
import ExamTimer from '@/components/exam/ExamTimer';
import { ConfirmSubmitDialog, TimeWarningDialog, ExitConfirmDialog, BlockEndDialog } from '@/components/exam/ExamDialogs';
import { 
  PMP_BLOCK_SIZE, 
  PMP_BREAK_MINUTES, 
  getExamConfig, 
  getCurrentBlockInfo,
  isQuestionInCurrentBlock as checkQuestionInCurrentBlock,
  isQuestionInCompletedBlock as checkQuestionInCompletedBlock
} from '@/components/exam/examConfig';

const ExamMode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Main state
  const [step, setStep] = useState('select-mock'); // select-mock, exam, break
  const [examType, setExamType] = useState(location.state?.examType || user?.selected_track || 'PMP');
  const [loading, setLoading] = useState(false);
  
  // Mock exam selection
  const [mockExams, setMockExams] = useState([]);
  const [selectedMockExam, setSelectedMockExam] = useState(null);
  const [loadingMockExams, setLoadingMockExams] = useState(true);
  
  // Available tracks for user
  const [availableTracks, setAvailableTracks] = useState([]);
  
  // Exam state
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeLimit, setTimeLimit] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  // Dialog states
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showBlockEndDialog, setShowBlockEndDialog] = useState(false);
  
  // Block-based exam state (PMP uses 3 blocks of 60 questions)
  const [currentBlock, setCurrentBlock] = useState(1);
  const [blockCompleted, setBlockCompleted] = useState([false, false, false]);
  const [blockAnswers, setBlockAnswers] = useState([{}, {}, {}]);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(0);
  
  // Question stats
  const [questionStats, setQuestionStats] = useState({});

  // Fetch available tracks for user (admins get all)
  useEffect(() => {
    const fetchTracks = async () => {
      if (isAdmin) {
        try {
          const response = await fetch(`${API}/categories`);
          if (response.ok) {
            const data = await response.json();
            const categoriesArray = data.categories || data;
            const courses = categoriesArray.map(c => c.id || c.code || c.name).filter(Boolean);
            if (courses.length > 0) {
              setAvailableTracks(courses);
            }
          }
        } catch (error) {
          console.error('Failed to fetch courses:', error);
        }
      } else {
        // Regular users get tracks from track_access
        const userTrackAccess = user?.track_access || [];
        const activeTracks = userTrackAccess.filter(t => t.is_active).map(t => t.track);
        if (activeTracks.length > 0) {
          setAvailableTracks(activeTracks);
        }
      }
    };
    fetchTracks();
  }, [isAdmin, user?.track_access]);

  // Fetch mock exams for selected track
  useEffect(() => {
    const fetchMockExams = async () => {
      setLoadingMockExams(true);
      try {
        const response = await fetch(`${API}/mock-exams?track=${examType}`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setMockExams(data);
        }
      } catch (error) {
        console.error('Failed to fetch mock exams:', error);
        toast.error('Failed to load mock exams');
      } finally {
        setLoadingMockExams(false);
      }
    };
    
    if (examType) {
      fetchMockExams();
    }
  }, [examType, getAuthHeaders]);

  // Fetch question stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/questions/stats`);
        if (response.ok) {
          const stats = await response.json();
          setQuestionStats(stats);
        }
      } catch (error) {
        console.error('Failed to fetch question stats:', error);
      }
    };
    fetchStats();
  }, []);

  // Build exam configs with real question counts
  const examConfigs = {
    PMP: getExamConfig('PMP', questionStats),
    CAPM: getExamConfig('CAPM', questionStats),
    PSM: getExamConfig('PSM', questionStats),
    PSPO: getExamConfig('PSPO', questionStats),
  };

  // Get current block info
  const blockInfo = getCurrentBlockInfo(examType, examConfigs, currentBlock);

  // Timer effect - handles both exam and break timers
  useEffect(() => {
    if (step === 'break' && breakTimeRemaining > 0) {
      const timer = setInterval(() => {
        setBreakTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            continueToNextBlock();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
    
    if (step !== 'exam' || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitExam();
          return 0;
        }
        if (prev === 300) {
          setShowTimeWarning(true);
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, timeRemaining, breakTimeRemaining]);

  // Continue to next block after break
  const continueToNextBlock = () => {
    setCurrentBlock(prev => prev + 1);
    if (blockInfo) {
      setCurrentIndex(blockInfo.blockEnd);
    }
    setStep('exam');
  };

  // End current block and start break
  const endCurrentBlock = () => {
    if (!blockInfo) return;
    
    // Lock answers for current block
    const newBlockAnswers = [...blockAnswers];
    const currentBlockAnswers = {};
    for (let i = blockInfo.blockStart; i < blockInfo.blockEnd; i++) {
      const q = questions[i];
      if (q && answers[q.question_id]) {
        currentBlockAnswers[q.question_id] = answers[q.question_id];
      }
    }
    newBlockAnswers[currentBlock - 1] = currentBlockAnswers;
    setBlockAnswers(newBlockAnswers);
    
    // Mark block as completed
    const newBlockCompleted = [...blockCompleted];
    newBlockCompleted[currentBlock - 1] = true;
    setBlockCompleted(newBlockCompleted);
    
    if (blockInfo.isLastBlock) {
      handleSubmitExam();
    } else {
      setBreakTimeRemaining(PMP_BREAK_MINUTES * 60);
      setStep('break');
    }
    
    setShowBlockEndDialog(false);
  };

  // Start mock exam
  const startExam = async () => {
    if (!selectedMockExam) {
      toast.error('Please select a mock exam first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/mock-exams/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          mock_exam_id: selectedMockExam.mock_exam_id
        })
      });

      // Read body only once
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to start exam');
      }

      setAttemptId(data.attempt_id);
      setQuestions(data.questions);
      setTimeLimit(data.time_limit * 60);
      setTimeRemaining(data.time_limit * 60);
      setStep('exam');
      
      toast.success(`Starting ${selectedMockExam.name}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];

  // Handle answer selection
  const handleAnswer = (value) => {
    const questionId = currentQuestion.question_id;
    let newAnswer;
    
    if (currentQuestion.question_type === 'multi') {
      const current = answers[questionId] || [];
      if (current.includes(value)) {
        newAnswer = current.filter(v => v !== value);
      } else {
        newAnswer = [...current, value];
      }
    } else {
      newAnswer = [value];
    }
    
    setAnswers(prev => ({ ...prev, [questionId]: newAnswer }));
  };

  // Save answer to server
  const saveAnswer = async () => {
    if (!currentQuestion) return;
    const questionId = currentQuestion.question_id;
    const userAnswer = answers[questionId] || [];
    
    if (userAnswer.length === 0) return;

    try {
      if (selectedMockExam) {
        await fetch(`${API}/mock-exams/answer/${attemptId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({
            question_id: questionId,
            selected_answers: userAnswer
          })
        });
      }
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  // Toggle flag on current question
  const toggleFlag = () => {
    const questionId = currentQuestion.question_id;
    setFlagged(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Navigate to specific question
  const goToQuestion = (index) => {
    saveAnswer();
    setCurrentIndex(index);
  };

  // Exit exam
  const handleExitExam = () => {
    setShowExitConfirm(false);
    setAttemptId(null);
    setQuestions([]);
    setCurrentIndex(0);
    setAnswers({});
    setFlagged(new Set());
    setTimeRemaining(0);
    setCurrentBlock(1);
    setBlockCompleted([false, false, false]);
    setBlockAnswers([{}, {}, {}]);
    setSelectedMockExam(null);
    setStep('select-mock');
    toast.info('Exam cancelled. Your progress was not saved.');
  };

  // Submit exam
  const handleSubmitExam = async () => {
    setShowConfirmSubmit(false);
    setLoading(true);
    
    await saveAnswer();
    
    try {
      const endpoint = `${API}/mock-exams/submit/${attemptId}`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ time_used: timeLimit - timeRemaining })
      });

      if (response.ok) {
        const result = await response.json();
        navigate(`/results/${attemptId}`, { 
          state: { mockExamResult: result }
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit exam');
      }
    } catch (error) {
      toast.error('Failed to submit exam');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for navigation
  const isInCurrentBlock = (idx) => checkQuestionInCurrentBlock(idx, blockInfo);
  const isInCompletedBlock = (idx) => checkQuestionInCompletedBlock(idx, currentBlock, PMP_BLOCK_SIZE);

  // Calculate stats for dialogs
  const unansweredCount = questions.filter(q => !answers[q.question_id]?.length).length;
  const flaggedCount = flagged.size;

  // ===== RENDER =====

  // Selection Screen
  if (step === 'select-mock') {
    return (
      <MockExamSelector
        examType={examType}
        setExamType={setExamType}
        mockExams={mockExams}
        loadingMockExams={loadingMockExams}
        selectedMockExam={selectedMockExam}
        setSelectedMockExam={setSelectedMockExam}
        loading={loading}
        onStart={startExam}
        onBack={() => navigate('/dashboard')}
        availableTracks={availableTracks}
      />
    );
  }

  // Break Screen
  if (step === 'break') {
    return (
      <BreakScreen
        breakTimeRemaining={breakTimeRemaining}
        currentBlock={currentBlock}
        totalBlocks={blockInfo?.totalBlocks || 3}
        onContinue={continueToNextBlock}
      />
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  // Exam Screen
  return (
    <ContentProtection isAdmin={isAdmin} userEmail={user?.email} userName={user?.name}>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowExitConfirm(true)}
              className="text-slate-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Exit
            </Button>
            
            {blockInfo && (
              <Badge variant="outline" className="text-[#0047AB]">
                Block {blockInfo.currentBlock} of {blockInfo.totalBlocks}
              </Badge>
            )}
          </div>
          
          <ExamTimer timeRemaining={timeRemaining} />
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">
              {Object.keys(answers).length} / {questions.length} answered
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-4">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Question Area */}
          <div className="lg:col-span-3 space-y-4">
            <QuestionView
              question={currentQuestion}
              questionIndex={currentIndex}
              totalQuestions={questions.length}
              selectedAnswers={answers[currentQuestion?.question_id]}
              onAnswer={handleAnswer}
            />

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => goToQuestion(currentIndex - 1)}
                disabled={currentIndex === 0 || (blockInfo && currentIndex <= blockInfo.blockStart)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  variant={flagged.has(currentQuestion?.question_id) ? "default" : "outline"}
                  onClick={toggleFlag}
                  className={flagged.has(currentQuestion?.question_id) ? "bg-orange-500 hover:bg-orange-600" : ""}
                >
                  <Flag className="w-4 h-4 mr-2" />
                  {flagged.has(currentQuestion?.question_id) ? 'Flagged' : 'Flag'}
                </Button>
              </div>

              {blockInfo && currentIndex === blockInfo.blockEnd - 1 ? (
                <Button
                  className="bg-[#0047AB] hover:bg-[#003380]"
                  onClick={() => setShowBlockEndDialog(true)}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {blockInfo.isLastBlock ? 'Submit Exam' : 'End Block'}
                </Button>
              ) : currentIndex === questions.length - 1 ? (
                <Button
                  className="bg-[#0047AB] hover:bg-[#003380]"
                  onClick={() => setShowConfirmSubmit(true)}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Submit Exam
                </Button>
              ) : (
                <Button
                  onClick={() => goToQuestion(currentIndex + 1)}
                  disabled={blockInfo && currentIndex >= blockInfo.blockEnd - 1}
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar - Question Navigator */}
          <div className="lg:col-span-1">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 sticky top-24">
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 text-sm">Question Navigator</h3>
                <ExamNavigation
                  questions={questions}
                  currentIndex={currentIndex}
                  answers={answers}
                  flagged={flagged}
                  blockInfo={blockInfo}
                  onNavigate={goToQuestion}
                  isQuestionInCurrentBlock={isInCurrentBlock}
                  isQuestionInCompletedBlock={isInCompletedBlock}
                />
                
                {/* Legend */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#0047AB]"></div>
                    <span>Current</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#0047AB]/20"></div>
                    <span>Answered</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-orange-500 fill-orange-500" />
                    <span>Flagged</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progress</span>
                    <span>{Math.round((Object.keys(answers).length / questions.length) * 100)}%</span>
                  </div>
                  <Progress value={(Object.keys(answers).length / questions.length) * 100} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      <ConfirmSubmitDialog
        open={showConfirmSubmit}
        onOpenChange={setShowConfirmSubmit}
        onConfirm={handleSubmitExam}
        unansweredCount={unansweredCount}
        flaggedCount={flaggedCount}
      />
      
      <TimeWarningDialog
        open={showTimeWarning}
        onOpenChange={setShowTimeWarning}
      />
      
      <ExitConfirmDialog
        open={showExitConfirm}
        onOpenChange={setShowExitConfirm}
        onConfirm={handleExitExam}
      />
      
      <BlockEndDialog
        open={showBlockEndDialog}
        onOpenChange={setShowBlockEndDialog}
        onConfirm={endCurrentBlock}
        blockNumber={currentBlock}
        totalBlocks={blockInfo?.totalBlocks || 3}
        isLastBlock={blockInfo?.isLastBlock}
      />
    </div>
    </ContentProtection>
  );
};

export default ExamMode;
